import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, BookOpen, DollarSign, FileText, Plus, Calendar,
  Download, TrendingUp, Building2, Percent, Edit, Trash2, Save,
  X, Check, AlertCircle, Eye, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MAX_WORKS = 5;

// Formatos disponibles
const WORK_FORMATS = [
  { id: 'printed', label: 'Libro Impreso', icon: '📖' },
  { id: 'ebook', label: 'eBook / EPUB', icon: '📱' },
  { id: 'audiobook', label: 'Audiolibro', icon: '🎧' },
  { id: 'videobook', label: 'Videolibro', icon: '🎬' }
];

const AuthorRoyalties = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('reports'); // 'reports' or 'works'
  
  // Reports state
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loadingReports, setLoadingReports] = useState(true);
  
  // Works state
  const [works, setWorks] = useState([]);
  const [loadingWorks, setLoadingWorks] = useState(true);
  const [showAddWork, setShowAddWork] = useState(false);
  const [newWork, setNewWork] = useState({
    title: '',
    formats: [], // Array of { format: 'audiobook', isbn: '...' }
    total_investment: 0,
    coproducers: [],
    // Campos para perfil público
    public_url: '',
    cover_image: '',
    show_in_profile: false
  });
  const [savingWork, setSavingWork] = useState(false);
  const [editingWorkId, setEditingWorkId] = useState(null);

  useEffect(() => {
    if (token && user?.user_type === 'author') {
      fetchReports();
      fetchWorks();
    }
  }, [token, user]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(
        `${API}/api/royalties/my-reports`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchWorks = async () => {
    try {
      const response = await axios.get(
        `${API}/api/works/my-works`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWorks(response.data.works || []);
    } catch (error) {
      console.error('Error fetching works:', error);
    } finally {
      setLoadingWorks(false);
    }
  };

  const resetWorkForm = () => {
    setNewWork({ title: '', formats: [], total_investment: 0, coproducers: [], public_url: '', cover_image: '', show_in_profile: false });
    setEditingWorkId(null);
  };

  const handleOpenCreate = () => {
    if (works.length >= MAX_WORKS) {
      toast.error('Has alcanzado el máximo de 5 obras. Adquiere paquetes adicionales por $697 en Cotizaciones.Viubux.com');
      return;
    }
    resetWorkForm();
    setShowAddWork(true);
  };

  const handleOpenEdit = (work) => {
    const formats = (work.formats || []).map(f =>
      typeof f === 'object' ? { format: f.format, isbn: f.isbn || '', url: f.url || '' } : { format: f, isbn: '', url: '' }
    );
    setNewWork({
      title: work.title || '',
      formats,
      total_investment: work.total_investment || 0,
      coproducers: work.coproducers || [],
      public_url: work.public_url || '',
      cover_image: work.cover_image || '',
      show_in_profile: !!work.show_in_profile,
    });
    setEditingWorkId(work.id);
    setShowAddWork(true);
  };

  const handleSaveWork = async () => {
    if (!newWork.title.trim()) {
      toast.error('El título es requerido');
      return;
    }

    setSavingWork(true);
    try {
      if (editingWorkId) {
        const response = await axios.put(
          `${API}/api/works/${editingWorkId}`,
          newWork,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setWorks(works.map(w => (w.id === editingWorkId ? response.data.work : w)));
        toast.success('Obra actualizada exitosamente');
      } else {
        const response = await axios.post(
          `${API}/api/works/create`,
          newWork,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setWorks([response.data.work, ...works]);
        toast.success('Obra registrada exitosamente');
      }
      setShowAddWork(false);
      resetWorkForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la obra');
    } finally {
      setSavingWork(false);
    }
  };

  const handleDeleteWork = async (work) => {
    const ok = window.confirm(
      `¿Eliminar "${work.title}"?\n\nIMPORTANTE: Si eliminas la obra o cambias su ISBN, podría afectar cómo se aplican las ventas a tu reporte y algunas ventas podrían perderse.\n\n¿Deseas continuar?`
    );
    if (!ok) return;
    try {
      await axios.delete(
        `${API}/api/works/${work.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWorks(works.filter(w => w.id !== work.id));
      toast.success('Obra eliminada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la obra');
    }
  };

  const toggleFormat = (formatId) => {
    const existingFormat = newWork.formats.find(f => f.format === formatId);
    if (existingFormat) {
      // Remove format
      const formats = newWork.formats.filter(f => f.format !== formatId);
      setNewWork({ ...newWork, formats });
    } else {
      // Add format with empty ISBN
      const formats = [...newWork.formats, { format: formatId, isbn: '', url: '' }];
      setNewWork({ ...newWork, formats });
    }
  };

  const updateFormatIsbn = (formatId, isbn) => {
    const formats = newWork.formats.map(f => 
      f.format === formatId ? { ...f, isbn } : f
    );
    setNewWork({ ...newWork, formats });
  };

  const updateFormatUrl = (formatId, url) => {
    const formats = newWork.formats.map(f =>
      f.format === formatId ? { ...f, url } : f
    );
    setNewWork({ ...newWork, formats });
  };

  const isFormatSelected = (formatId) => {
    return newWork.formats.some(f => f.format === formatId);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Redirect if not author
  if (user && user.user_type !== 'author') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #080c2d 0%, #1a1a3e 100%)' }}>
        <Card className="card-viubux-dark border-red-500/30 max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-white text-xl gotham-bold mb-2">Acceso Restringido</h2>
            <p className="text-white/60 mb-4">Esta sección está disponible solo para autores.</p>
            <Button onClick={() => navigate('/dashboard')} className="btn-viubux">
              Volver al Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #080c2d 0%, #1a1a3e 100%)' }}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="border-purple-500/50 text-white hover:bg-purple-500/20"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="gotham-bold text-3xl text-white flex items-center gap-3">
                <Building2 className="w-8 h-8 text-green-400" />
                5BX PUBLISHING & DISTRIBUTION
              </h1>
              <p className="text-white/60 verdana-regular">
                Reportes de Regalías y Gestión de Obras
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('reports')}
            className={`${activeTab === 'reports' ? 'btn-viubux' : 'bg-white/10 text-white hover:bg-white/20'}`}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Mis Regalías
          </Button>
          <Button
            onClick={() => setActiveTab('works')}
            className={`${activeTab === 'works' ? 'btn-viubux' : 'bg-white/10 text-white hover:bg-white/20'}`}
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Mis Obras
          </Button>
        </div>

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="card-viubux-dark border-green-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/60 text-sm">Total Regalías</p>
                      <p className="text-green-400 gotham-bold text-2xl">
                        {formatCurrency(reports.reduce((sum, r) => sum + (r.total_net_royalty || 0), 0))}
                      </p>
                    </div>
                    <DollarSign className="w-10 h-10 text-green-400/30" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="card-viubux-dark border-blue-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/60 text-sm">Unidades Vendidas</p>
                      <p className="text-blue-400 gotham-bold text-2xl">
                        {reports.reduce((sum, r) => sum + (r.total_units || 0), 0).toLocaleString()}
                      </p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-blue-400/30" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="card-viubux-dark border-purple-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/60 text-sm">Reportes Disponibles</p>
                      <p className="text-purple-400 gotham-bold text-2xl">
                        {reports.length}
                      </p>
                    </div>
                    <FileText className="w-10 h-10 text-purple-400/30" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Reports List */}
            <Card className="card-viubux-dark border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-400" />
                  Historial de Reportes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingReports ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-white/60 mt-4">Cargando reportes...</p>
                  </div>
                ) : reports.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-16 h-16 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60">No hay reportes de regalías disponibles</p>
                    <p className="text-white/40 text-sm mt-2">Los reportes aparecerán aquí cuando sean publicados por el administrador</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reports.map(report => (
                      <div 
                        key={report.id}
                        className="bg-black/30 border border-purple-500/20 rounded-lg p-4 hover:border-purple-500/40 transition-colors cursor-pointer"
                        onClick={() => setSelectedReport(report)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white gotham-bold">
                              Período: {report.period_start} al {report.period_end}
                            </p>
                            <p className="text-white/60 text-sm">
                              {report.period_type === 'monthly' ? 'Mensual' : 
                               report.period_type === 'quarterly' ? 'Trimestral' : 'Semestral'}
                              {' • '}{report.entries?.length || 0} entradas
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-400 gotham-bold">{formatCurrency(report.total_net_royalty)}</p>
                            <p className="text-white/40 text-sm">{report.total_units} unidades</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            report.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            report.status === 'published' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {report.status === 'paid' ? 'Pagado' : 
                             report.status === 'published' ? 'Publicado' : 'Borrador'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Report Detail Modal */}
            {selectedReport && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-white/10 flex items-center justify-between">
                    <div>
                      <h2 className="gotham-bold text-xl text-white">Detalle del Reporte</h2>
                      <p className="text-white/60 text-sm">
                        {selectedReport.period_start} al {selectedReport.period_end}
                      </p>
                    </div>
                    <button onClick={() => setSelectedReport(null)} className="text-white/60 hover:text-white">
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="p-6">
                    {/* Summary */}
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm">Regalía Neta</p>
                        <p className="text-green-400 gotham-bold text-xl">{formatCurrency(selectedReport.total_net_royalty)}</p>
                      </div>
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm">Ingreso Bruto</p>
                        <p className="text-blue-400 gotham-bold text-xl">{formatCurrency(selectedReport.total_gross)}</p>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm">Unidades</p>
                        <p className="text-purple-400 gotham-bold text-xl">{selectedReport.total_units.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Entries Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left text-white/60 text-sm py-3 px-2">ISBN</th>
                            <th className="text-left text-white/60 text-sm py-3 px-2">Obra</th>
                            <th className="text-left text-white/60 text-sm py-3 px-2">Plataforma</th>
                            <th className="text-right text-white/60 text-sm py-3 px-2">Unidades</th>
                            <th className="text-right text-white/60 text-sm py-3 px-2">Bruto</th>
                            <th className="text-right text-white/60 text-sm py-3 px-2">Neto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedReport.entries?.map((entry, idx) => (
                            <tr key={idx} className="border-b border-white/5">
                              <td className="text-white/80 text-sm py-3 px-2 font-mono">{entry.isbn}</td>
                              <td className="text-white py-3 px-2">
                                {entry.work_title}
                                {entry.is_editorial_split && (
                                  <span className="ml-2 text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full whitespace-nowrap">
                                    Split editorial ({entry.split_percent}%){entry.source_editorial_name ? ` · ${entry.source_editorial_name}` : ''}
                                  </span>
                                )}
                              </td>
                              <td className="text-purple-400 py-3 px-2">{entry.platform}</td>
                              <td className="text-white text-right py-3 px-2">{entry.units_sold}</td>
                              <td className="text-white/60 text-right py-3 px-2">{formatCurrency(entry.gross_income)}</td>
                              <td className="text-green-400 text-right py-3 px-2 gotham-bold">{formatCurrency(entry.net_royalty)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Breakdown */}
                    <div className="mt-6 bg-black/30 rounded-lg p-4">
                      <h4 className="text-white gotham-bold mb-3">Desglose de Deducciones (Última entrada)</h4>
                      {selectedReport.entries?.length > 0 && (
                        <div className="grid md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-white/50">Retención Plataforma (15%)</p>
                            <p className="text-red-400">{formatCurrency(selectedReport.entries[0].platform_retention)}</p>
                          </div>
                          <div>
                            <p className="text-white/50">Fee Plataforma (33% Viubux)</p>
                            <p className="text-red-400">{formatCurrency(selectedReport.entries[0].platform_fee)}</p>
                          </div>
                          <div>
                            <p className="text-white/50">Agente 5BX (15%)</p>
                            <p className="text-red-400">{formatCurrency(selectedReport.entries[0].agent_fee)}</p>
                          </div>
                          <div>
                            <p className="text-white/50">Impuestos (16%)</p>
                            <p className="text-red-400">{formatCurrency(selectedReport.entries[0].tax)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Works Tab */}
        {activeTab === 'works' && (
          <div className="space-y-6">
            {/* Add Work Button + Counter */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2" data-testid="works-counter">
                <BookOpen className="w-5 h-5 text-purple-400" />
                <span className="text-white font-semibold">Mis Obras</span>
                <span className={`text-sm px-2 py-0.5 rounded-full ${works.length >= MAX_WORKS ? 'bg-red-500/20 text-red-300' : 'bg-purple-500/20 text-purple-300'}`}>
                  {works.length}/{MAX_WORKS}
                </span>
              </div>
              <Button
                onClick={handleOpenCreate}
                disabled={works.length >= MAX_WORKS}
                className="btn-viubux disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="create-work-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Registrar Nueva Obra
              </Button>
            </div>

            {/* Límite alcanzado */}
            {works.length >= MAX_WORKS && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3" data-testid="works-limit-banner">
                <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-200">
                  <p className="font-semibold">Has alcanzado el máximo de 5 obras.</p>
                  <p className="text-amber-200/80 mt-1">
                    Puedes adquirir paquetes adicionales por $697 en{' '}
                    <a href="https://cotizaciones.viubux.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-100">Cotizaciones.Viubux.com</a>
                    {' '}o en{' '}
                    <a href="https://vbx.viubux.com/presentacion-distribucion" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-100">vbx.viubux.com/presentacion-distribucion</a>.
                  </p>
                </div>
              </div>
            )}

            {/* Works List */}
            <Card className="card-viubux-dark border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-purple-400" />
                  Mis Obras Registradas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingWorks ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                  </div>
                ) : works.length === 0 ? (
                  <div className="text-center py-8">
                    <BookOpen className="w-16 h-16 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60">No tienes obras registradas</p>
                    <p className="text-white/40 text-sm mt-2">Registra tus obras para recibir reportes de regalías</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {works.map(work => (
                      <div 
                        key={work.id}
                        className={`bg-black/30 border rounded-lg p-4 ${work.show_in_profile ? 'border-green-500/30' : 'border-purple-500/20'}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start gap-3">
                            {/* Portada */}
                            {work.cover_image ? (
                              <img 
                                src={work.cover_image} 
                                alt={work.title}
                                className="w-12 h-16 object-cover rounded border border-purple-500/30"
                              />
                            ) : null}
                            <div>
                              <h4 className="text-white gotham-bold">{work.title}</h4>
                              <p className="text-purple-400 text-sm font-mono">{work.isbn}</p>
                            </div>
                          </div>
                          {work.show_in_profile && (
                            <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded flex items-center gap-1">
                              <Eye className="w-3 h-3" /> En perfil
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {work.formats?.map((format, idx) => {
                            // Manejar formato nuevo (objeto) y antiguo (string)
                            const formatId = typeof format === 'object' ? format.format : format;
                            const formatIsbn = typeof format === 'object' ? format.isbn : null;
                            const f = WORK_FORMATS.find(wf => wf.id === formatId);
                            return f ? (
                              <div key={idx} className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                                <div className="flex items-center gap-1">
                                  {f.icon} {f.label}
                                </div>
                                {formatIsbn && (
                                  <div className="text-purple-300/60 text-[10px] font-mono mt-0.5">
                                    {formatIsbn}
                                  </div>
                                )}
                              </div>
                            ) : null;
                          })}
                        </div>
                        
                        {work.public_url && (
                          <a 
                            href={work.public_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-purple-400 text-sm hover:text-purple-300 flex items-center gap-1 mb-2"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Ver en app.viubux.com
                          </a>
                        )}
                        
                        {work.has_coproduction && (
                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2 text-sm">
                            <p className="text-yellow-400">
                              <Percent className="w-3 h-3 inline mr-1" />
                              Coproducción: Tu parte {work.author_percentage}%
                            </p>
                          </div>
                        )}
                        
                        <p className="text-white/40 text-xs mt-3">
                          Registrada: {formatDate(work.created_at)}
                        </p>

                        <div className="flex gap-2 mt-3 pt-3 border-t border-white/10">
                          <Button
                            onClick={() => handleOpenEdit(work)}
                            size="sm"
                            variant="outline"
                            className="flex-1 border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
                            data-testid={`edit-work-${work.id}`}
                          >
                            <Edit className="w-3 h-3 mr-1" /> Editar
                          </Button>
                          <Button
                            onClick={() => handleDeleteWork(work)}
                            size="sm"
                            variant="outline"
                            className="flex-1 border-red-500/40 text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-work-${work.id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Work Modal */}
            {showAddWork && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-white/10 sticky top-0 bg-gray-900 z-10">
                    <h2 className="gotham-bold text-xl text-white">{editingWorkId ? 'Editar Obra' : 'Registrar Nueva Obra'}</h2>
                    <p className="text-white/60 text-sm">Ingresa los datos de tu obra para recibir reportes de regalías</p>
                    {editingWorkId && (
                      <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                        <p className="text-amber-200 text-xs">
                          Importante: si cambias o eliminas un ISBN, podría afectar cómo se aplican las ventas a tu reporte y algunas ventas podrían perderse.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Título de la Obra *</label>
                      <input
                        type="text"
                        value={newWork.title}
                        onChange={(e) => setNewWork({...newWork, title: e.target.value})}
                        className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                        placeholder="Ej: Mi Gran Audiolibro"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Formatos Disponibles *</label>
                      <p className="text-white/40 text-xs mb-3">Selecciona los formatos de tu obra. Cada formato requiere su ISBN específico.</p>
                      <div className="grid grid-cols-2 gap-2">
                        {WORK_FORMATS.map(format => (
                          <div
                            key={format.id}
                            onClick={() => toggleFormat(format.id)}
                            className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                              isFormatSelected(format.id)
                                ? 'bg-purple-500/20 border-purple-500 text-purple-400'
                                : 'bg-black/30 border-white/10 text-white/60 hover:border-purple-500/50'
                            }`}
                          >
                            <span className="text-lg mr-2">{format.icon}</span>
                            <span className="text-sm">{format.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ISBN por cada formato seleccionado */}
                    {newWork.formats.length > 0 && (
                      <div className="bg-purple-500/5 border border-purple-500/30 rounded-lg p-4 space-y-4">
                        <h4 className="text-purple-400 font-semibold flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          ISBN y Link de lectura por Formato
                        </h4>
                        <p className="text-white/50 text-xs">Ingresa el ISBN de cada formato (si no tienes, se generará uno VBX-...). El <strong>link de lectura</strong> se agregará automáticamente a tu página pública (connect.viubux.com/tu-usuario).</p>
                        
                        {newWork.formats.map(formatItem => {
                          const formatInfo = WORK_FORMATS.find(f => f.id === formatItem.format);
                          return (
                            <div key={formatItem.format} className="bg-black/20 border border-purple-500/20 rounded-lg p-3 space-y-2">
                              <div className="flex items-center gap-2">
                                <span>{formatInfo?.icon}</span>
                                <span className="text-white/80 text-sm font-semibold">{formatInfo?.label}</span>
                              </div>
                              <input
                                type="text"
                                value={formatItem.isbn}
                                onChange={(e) => updateFormatIsbn(formatItem.format, e.target.value)}
                                className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500 font-mono text-sm"
                                placeholder="ISBN (opcional)"
                                data-testid={`format-isbn-${formatItem.format}`}
                              />
                              <input
                                type="text"
                                value={formatItem.url || ''}
                                onChange={(e) => updateFormatUrl(formatItem.format, e.target.value)}
                                className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500 text-sm"
                                placeholder="Link de lectura de este formato (https://...)"
                                data-testid={`format-url-${formatItem.format}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Inversión Total de Producción (MXN)</label>
                      <input
                        type="number"
                        value={newWork.total_investment}
                        onChange={(e) => setNewWork({...newWork, total_investment: parseFloat(e.target.value) || 0})}
                        className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                        placeholder="0"
                      />
                    </div>

                    {/* Campos para Perfil Público */}
                    <div className="bg-purple-500/5 border border-purple-500/30 rounded-lg p-4 space-y-4">
                      <h4 className="text-purple-400 font-semibold flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Perfil Público (Networking)
                      </h4>
                      <p className="text-white/50 text-xs">Estos campos permiten mostrar tu obra en tu perfil público de Networking</p>
                      
                      <div>
                        <label className="block text-white/70 text-sm mb-2">URL en app.viubux.com (opcional)</label>
                        <input
                          type="url"
                          value={newWork.public_url}
                          onChange={(e) => setNewWork({...newWork, public_url: e.target.value})}
                          className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                          placeholder="https://app.viubux.com/libro/..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white/70 text-sm mb-2">URL de la Portada (opcional)</label>
                        <input
                          type="url"
                          value={newWork.cover_image}
                          onChange={(e) => setNewWork({...newWork, cover_image: e.target.value})}
                          className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                          placeholder="https://..."
                        />
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="show_in_profile"
                          checked={newWork.show_in_profile}
                          onChange={(e) => setNewWork({...newWork, show_in_profile: e.target.checked})}
                          className="w-4 h-4 rounded border-purple-500/30 bg-black/30 text-purple-500 focus:ring-purple-500"
                        />
                        <label htmlFor="show_in_profile" className="text-white/70 text-sm">
                          Mostrar en mi perfil público de Networking
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6 border-t border-white/10 flex gap-3 sticky bottom-0 bg-gray-900">
                    <Button
                      onClick={() => { setShowAddWork(false); resetWorkForm(); }}
                      variant="outline"
                      className="flex-1 border-white/20 text-white hover:bg-white/10"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSaveWork}
                      disabled={savingWork}
                      className="flex-1 btn-viubux"
                    >
                      {savingWork ? 'Guardando...' : (editingWorkId ? 'Guardar Cambios' : 'Registrar Obra')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorRoyalties;
